import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Clock, ArrowLeft, Tag, Share2 } from 'lucide-react';
import { getPostBySlug, getRecentPosts } from '../data/blogPosts';

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : null;
  const recentPosts = getRecentPosts(3);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const sharePost = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title,
        text: post.excerpt,
        url: window.location.href,
      });
    } else {
      // Fallback: copy URL to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Back to Blog */}
            <Link 
              to="/blog"
              className="inline-flex items-center gap-2 text-primary-500 hover:text-primary-600 mb-8 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </Link>

            {/* Category Badge */}
            <div className="mb-4">
              <span 
                className={`inline-block px-3 py-1 rounded-full text-sm font-semibold bg-primary-500 text-white`}
              >
                {post.category.name}
              </span>
            </div>

            {/* Title */}
            <h1 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900 dark:text-white leading-tight">
              {post.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 text-slate-600 dark:text-gray-400 mb-8">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{post.author.name}</span>
                {post.author.title && (
                  <span className="text-sm">• {post.author.title}</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{new Date(post.publishedDate).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                <span>{post.readTime}</span>
              </div>
              <button
                onClick={sharePost}
                className="flex items-center gap-2 text-primary-500 hover:text-primary-600 transition-colors"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>

            {/* Excerpt */}
            <p className="text-xl text-slate-700 dark:text-gray-300 leading-relaxed">
              {post.excerpt}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured Image */}
      <section className="py-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="rounded-2xl overflow-hidden shadow-2xl"
          >
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-64 md:h-96 object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-20 bg-white dark:bg-gray-900/50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Main Content */}
            <motion.article
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="lg:col-span-3 prose prose-lg dark:prose-invert max-w-none"
            >
              <div 
                className="text-slate-800 dark:text-gray-200 leading-relaxed"
                style={{
                  lineHeight: '1.8',
                }}
              >
                {/* Render markdown-style content */}
                {(() => {
                  const renderMarkdownLine = (line: string): React.ReactNode => {
                    // Handle bold text **text**
                    let processedLine: React.ReactNode = line;
                    
                    if (typeof processedLine === 'string') {
                      const boldRegex = /\*\*(.*?)\*\*/g;
                      const parts = processedLine.split(boldRegex);
                      processedLine = parts.map((part, i) => 
                        i % 2 === 1 ? <strong key={i} className="font-bold">{part}</strong> : part
                      );
                    }
                    
                    return processedLine;
                  };

                  const lines = post.content.split('\n');
                  const elements: React.ReactNode[] = [];
                  let inUnorderedList = false;
                  let inOrderedList = false;
                  let currentListItems: React.ReactNode[] = [];

                  const flushList = () => {
                    if (currentListItems.length > 0) {
                      if (inUnorderedList) {
                        elements.push(
                          <ul key={`list-${elements.length}`} className="list-disc list-inside mb-4 space-y-2 text-slate-700 dark:text-gray-300 ml-4">
                            {currentListItems}
                          </ul>
                        );
                      } else if (inOrderedList) {
                        elements.push(
                          <ol key={`list-${elements.length}`} className="list-decimal list-inside mb-4 space-y-2 text-slate-700 dark:text-gray-300 ml-4">
                            {currentListItems}
                          </ol>
                        );
                      }
                      currentListItems = [];
                      inUnorderedList = false;
                      inOrderedList = false;
                    }
                  };

                  lines.forEach((paragraph, index) => {
                    if (paragraph.startsWith('# ')) {
                      flushList();
                      elements.push(
                        <h1 key={index} className="text-3xl font-bold mb-6 mt-8 text-slate-900 dark:text-white">
                          {renderMarkdownLine(paragraph.replace('# ', ''))}
                        </h1>
                      );
                    } else if (paragraph.startsWith('## ')) {
                      flushList();
                      elements.push(
                        <h2 key={index} className="text-2xl font-bold mb-4 mt-8 text-slate-900 dark:text-white">
                          {renderMarkdownLine(paragraph.replace('## ', ''))}
                        </h2>
                      );
                    } else if (paragraph.startsWith('### ')) {
                      flushList();
                      elements.push(
                        <h3 key={index} className="text-xl font-bold mb-3 mt-6 text-slate-900 dark:text-white">
                          {renderMarkdownLine(paragraph.replace('### ', ''))}
                        </h3>
                      );
                    } else if (paragraph.startsWith('#### ')) {
                      flushList();
                      elements.push(
                        <h4 key={index} className="text-lg font-bold mb-2 mt-4 text-slate-900 dark:text-white">
                          {renderMarkdownLine(paragraph.replace('#### ', ''))}
                        </h4>
                      );
                    } else if (paragraph.startsWith('- ') || paragraph.startsWith('* ')) {
                      if (!inUnorderedList) {
                        flushList();
                        inUnorderedList = true;
                      }
                      const content = paragraph.replace(/^[*-] /, '');
                      currentListItems.push(
                        <li key={`${index}-li`} className="mb-1">
                          {renderMarkdownLine(content)}
                        </li>
                      );
                    } else if (/^\d+\. /.test(paragraph)) {
                      if (!inOrderedList) {
                        flushList();
                        inOrderedList = true;
                      }
                      const content = paragraph.replace(/^\d+\. /, '');
                      currentListItems.push(
                        <li key={`${index}-li`} className="mb-1">
                          {renderMarkdownLine(content)}
                        </li>
                      );
                    } else if (paragraph.trim() === '') {
                      flushList();
                      elements.push(<div key={index} className="h-4" />);
                    } else if (paragraph.startsWith('*') && paragraph.endsWith('*') && !paragraph.includes('**')) {
                      flushList();
                      elements.push(
                        <p key={index} className="italic text-center my-6 text-slate-600 dark:text-gray-400 border-l-4 border-primary-500 pl-4">
                          {paragraph.replace(/^\*/, '').replace(/\*$/, '')}
                        </p>
                      );
                    } else if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                      flushList();
                      elements.push(
                        <p key={index} className="font-bold text-lg mb-4 text-slate-800 dark:text-gray-200">
                          {paragraph.replace(/^\*\*/, '').replace(/\*\*$/, '')}
                        </p>
                      );
                    } else {
                      flushList();
                      elements.push(
                        <p key={index} className="mb-4 text-slate-700 dark:text-gray-300">
                          {renderMarkdownLine(paragraph)}
                        </p>
                      );
                    }
                  });

                  flushList(); // Flush any remaining list
                  return elements;
                })()}
              </div>
            </motion.article>

            {/* Sidebar */}
            <motion.aside
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="lg:col-span-2"
            >
              {/* Tags */}
              <div className="glass-effect p-6 rounded-xl mb-8">
                <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-primary-500/20 text-primary-600 dark:text-primary-400 rounded-full text-sm"
                    >
                      <Tag className="w-3 h-3" />
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* Recent Posts */}
              <div className="glass-effect p-6 rounded-xl">
                <h3 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">Recent Posts</h3>
                <div className="space-y-4">
                  {recentPosts.filter(p => p.id !== post.id).slice(0, 3).map((recentPost, index) => (
                    <Link
                      key={recentPost.id}
                      to={`/blog/${recentPost.slug}`}
                      className="block group"
                    >
                      <div className="flex gap-3">
                        <img
                          src={recentPost.image}
                          alt={recentPost.title}
                          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
                        />
                        <div>
                          <h4 className="text-sm font-semibold text-slate-900 dark:text-white group-hover:text-primary-500 transition-colors line-clamp-2">
                            {recentPost.title}
                          </h4>
                          <p className="text-xs text-slate-600 dark:text-gray-400 mt-1">
                            {new Date(recentPost.publishedDate).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </motion.aside>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-slate-100/50 dark:bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="glass-effect p-8 rounded-2xl"
          >
            <h3 className="text-2xl font-bold mb-4 text-slate-900 dark:text-white">
              Ready to Transform Your Healthcare Practice?
            </h3>
            <p className="text-slate-700 dark:text-gray-300 mb-6">
              Discover how BioAxis can help streamline your operations and improve patient care.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="btn-primary"
              >
                Schedule a Demo
              </Link>
              <Link
                to="/blog"
                className="btn-outline text-slate-700 border-slate-700"
              >
                Read More Articles
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default BlogPost; 